<template>
  <li class="t-tab" :class="classes" @click="handleClick">
    <div class="t-tab-content">
      <div class="t-tab-label t-tab-label--regular">
        <slot>{{ label }}</slot>
      </div>
      <div class="t-tab-label t-tab-label--semibold">
        <slot>{{ label }}</slot>
      </div>
    </div>
    <div class="t-tab-highlight" />
  </li>
</template>

<script>
const types = {
  default: 'default',
  disabled: 'disabled',
  error: 'error',
  selected: 'selected',
};

export default {
  name: 't-tab',
  props: {
    actived: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    index: { type: String, default: 'index' },
    label: { type: String, default: 'label' },
    selected: { type: Boolean, default: false },
    type: {
      default: types.default,
      validator(value) {
        const options = [
          types.default,
          types.disabled,
          types.error,
          types.selected,
        ];
        return options.indexOf(value) !== -1;
      },
    },
  },
  emits: ['click'],
  computed: {
    classes() {
      return {
        'is-disabled': this.isDisabled,
        'is-error': this.isError,
        'is-selected': this.isSelected,
      };
    },
    isDisabled() {
      return this.type === types.disabled || this.disabled;
    },
    isError() {
      return this.type === types.error || this.error;
    },
    isSelected() {
      return (
        this.actived === this.index
        || this.type === types.selected
        || this.selected
      );
    },
  },
  methods: {
    handleClick() {
      if (this.isDisabled === false) {
        this.$emit('click', this.index);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.t-tab {
  align-items: center;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  position: relative;

  --t-tab-label-color: var(--track-color-bluegray-70);
  --t-tab-label-hovered-color: var(--track-color-bluegray-80);
  --t-tab-highlight-color: transparent;
  --t-tab-highlight-hovered-color: var(--track-color-bluegray-40);

  &[disabled],
  &.is-disabled {
    --t-tab-label-color: var(--track-color-bluegray-50);
    --t-tab-label-hovered-color: var(--track-color-bluegray-50);
    --t-tab-highlight-hovered-color: transparent;
    cursor: not-allowed;
  }

  &.is-error,
  &.is-error.is-selected {
    --t-tab-label-color: var(--track-color-negative-darkest);
    --t-tab-label-hovered-color: var(--track-color-negative-darkest);
    --t-tab-highlight-hovered-color: var(--track-color-negative-darkest);
    --t-tab-highlight-color: var(--track-color-negative-darkest);
  }

  &.is-error.is-selected {
    --t-tab-highlight-color: var(--track-color-negative-darkest);
    --t-tab-highlight-hovered-color: var(--track-color-negative-darkest);
  }

  &.is-selected {
    --t-tab-label-color: var(--track-color-bluegray-80);
    --t-tab-highlight-color: var(--track-color-brand-primary);
    --t-tab-highlight-hovered-color: var(--track-color-brand-primary);
  }
}

.t-tab-content {
  display: flex;
  justify-content: center;
  padding: 0 24px;
  position: relative;
}

.t-tab-label {
  color: var(--t-tab-label-color);
  font-size: 16px;
  text-align: center;
  transition: all 0.1s;
  user-select: none;
  line-height: 48px;

  .t-tab:hover & {
    color: var(--t-tab-label-hovered-color);
  }

  &.t-tab-label--regular {
    font-weight: var(--track-font-weight-regular);
    position: absolute;
    top: 0;

    .t-tab.is-selected & {
      opacity: 0;
    }
  }

  &.t-tab-label--semibold {
    font-weight: var(--track-font-weight-semibold);
    opacity: 0;

    .t-tab.is-selected & {
      opacity: 1;
    }
  }
}

.t-tab-highlight {
  background-color: var(--t-tab-highlight-color);
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  bottom: 0;
  height: 2px;
  left: 0;
  position: absolute;
  transition: all 0.5s;
  width: 100%;

  .t-tab:hover & {
    background-color: var(--t-tab-highlight-hovered-color);
  }
}
</style>
